// "AI with your own key" settings (M15). The key is stored only in this browser (IndexedDB 'epb-ai'), is
// never logged, and is never written into sidecars, designs or exports. Without a key every AI button is
// hidden and the app makes no network call (tested).
import { create } from 'zustand';
import { createStore, del, get, set, type UseStore } from 'idb-keyval';

export type AiProvider = 'openai' | 'google';
export type AiModels = { vision: string; image: string };
export type AiSettings = { provider: AiProvider; key: string; models: Record<AiProvider, AiModels> };

/** Defaults (the first choice of each list below). */
export const DEFAULT_MODELS: Record<AiProvider, AiModels> = {
  openai: { vision: 'gpt-4.1-mini', image: 'gpt-image-1' },
  google: { vision: 'gemini-2.5-flash', image: 'gemini-2.5-flash-image' },
};

/**
 * The models the AI screen offers (Your call Y10: a list instead of free text). Each one works with the requests
 * aiClient makes: OpenAI chat completions with a strict JSON schema, OpenAI image edits with size 'auto' (dall-e-2
 * would reject it), Gemini generateContent with a JSON schema, and Gemini image output. A provider renaming a
 * model means adding it here.
 */
export const MODEL_CHOICES: Record<AiProvider, Record<keyof AiModels, readonly string[]>> = {
  openai: { vision: ['gpt-4.1-mini', 'gpt-4.1', 'gpt-4o-mini', 'gpt-4o'], image: ['gpt-image-1'] },
  google: {
    vision: ['gemini-2.5-flash', 'gemini-2.5-pro', 'gemini-2.5-flash-lite'],
    image: ['gemini-2.5-flash-image'],
  },
};

/** Stored models outside the list (typed by hand before Y10) fall back to the default. */
export function validModels(m: Partial<Record<AiProvider, Partial<AiModels>>> | undefined) {
  const out = structuredClone(DEFAULT_MODELS);
  for (const p of ['openai', 'google'] as const)
    for (const k of ['vision', 'image'] as const) {
      const v = m?.[p]?.[k];
      if (v && MODEL_CHOICES[p][k].includes(v)) out[p][k] = v;
    }
  return out;
}

let db: UseStore | null = null;
const store = () => (db ??= createStore('epb-ai', 'ai'));
const KEY = 'settings';

type AiState = { settings: AiSettings | null; loaded: boolean };
export const useAi = create<AiState>(() => ({ settings: null, loaded: false }));

/** True when a key is set (the AI buttons show). */
export const aiReady = () => !!useAi.getState().settings?.key;

export async function loadAi(): Promise<AiSettings | null> {
  if (useAi.getState().loaded) return useAi.getState().settings;
  const s = (await get<AiSettings>(KEY, store()).catch(() => undefined)) ?? null;
  const settings = s && { ...s, models: validModels(s.models) };
  useAi.setState({ settings, loaded: true });
  return settings;
}

export async function saveAi(s: AiSettings): Promise<void> {
  const clean = { ...s, key: s.key.trim() };
  useAi.setState({ settings: clean.key ? clean : null, loaded: true });
  // Storage can be unavailable (private window): the key then lasts for this session only.
  if (clean.key) await set(KEY, clean, store()).catch(() => undefined);
  else await del(KEY, store()).catch(() => undefined);
}

/** "Remove key": forgets it in this browser. */
export async function removeAiKey(): Promise<void> {
  useAi.setState({ settings: null, loaded: true });
  await del(KEY, store()).catch(() => undefined);
}

/** A key shown on screen: first 3 and last 4 characters only. */
export const maskKey = (k: string) => (k.length <= 10 ? '••••' : `${k.slice(0, 3)}…${k.slice(-4)}`);
